import type {SxProps, Theme} from '@mui/material/styles';
import {colors, fontFamily, fontWeight} from '@/styles/tokens';

/**
 * Case-study template styles as MUI `sx` objects — the direct port of what used
 * to live in `caseStudyTemplate.module.scss`. Import a key and spread it into a
 * component's `sx` (compose with arrays, e.g. `sx={[csx.card, csx.cardLift]}`).
 *
 * Per-study theming is unchanged: values still read the `--cs-*` CSS variables
 * the template sets on the root (see `rootVars`), so reskinning a study still
 * means editing only its `theme`.
 *
 * Notes on the port:
 *  - `.sectionDark`'s old nested `.heading/.subheading` color override is now
 *    driven by the `onDark` prop on `SectionHeader` (and an explicit on-dark
 *    color where a section renders its heading directly).
 *  - Solution's pinned-scroll elements keep working via `data-sol="…"` hooks
 *    that GSAP selects on (class-based selectors are impossible with hashed
 *    emotion class names).
 */

// Fixed design-system CSS variables (not per-study). Set once on the template
// root; every section reads them. Per-study vars (--cs-dark-bg, --cs-heading, …)
// are merged in by CaseStudyTemplate from `data.theme`.
export const rootVars: Record<string, string> = {
    '--cs-on-dark': '#ffffff',
    '--cs-body': '#111111',
    '--cs-surface': '#ffffff',
    '--cs-surface-subtle': '#f8fafc',
    '--cs-surface-muted': '#f4f4f6',
    '--cs-surface-chip': '#f3f4f6',
    '--cs-surface-icon': '#f1f5f9',
    '--cs-danger-bg': '#fef2f2',
    '--cs-danger-fg': '#991b1b',
    '--cs-danger-icon': '#dc2626',
    '--cs-success-bg': '#f0fdf4',
    '--cs-success-fg': '#166534',
    '--cs-success-icon': '#16a34a',
    '--cs-shadow-sm': '0 4px 14px rgba(16, 24, 40, 0.04)',
    '--cs-shadow-md': '0 8px 24px rgba(16, 24, 40, 0.06)',
    '--cs-glass-fill-weak': 'rgba(255, 255, 255, 0.05)',
    '--cs-glass-fill': 'rgba(255, 255, 255, 0.06)',
    '--cs-glass-fill-strong': 'rgba(255, 255, 255, 0.08)',
    '--cs-glass-fill-icon': 'rgba(255, 255, 255, 0.1)',
    '--cs-glass-border': 'rgba(255, 255, 255, 0.12)',
    '--cs-glass-border-14': 'rgba(255, 255, 255, 0.14)',
    '--cs-glass-border-18': 'rgba(255, 255, 255, 0.18)',
    '--cs-glass-divider': 'rgba(255, 255, 255, 0.25)',
    '--cs-glass-border-35': 'rgba(255, 255, 255, 0.35)',
    '--cs-glass-border-strong': 'rgba(255, 255, 255, 0.5)'
};

export const csx = {
    // ── Template root ────────────────────────────────────────────────────────
    root: {
        ...rootVars,
        width: '100%',
        // `clip` (not `hidden`) guards sideways scroll WITHOUT turning this into a
        // scroll container — `overflow-x: hidden` computes overflow-y to `auto`,
        // which breaks GSAP ScrollTrigger pinning in the Solution section.
        overflowX: 'clip',
        // nft-button carries no text color of its own, so MUI's default (blue)
        // shows through. Case-study hero buttons always use white text.
        '& .nft-button': {color: 'var(--cs-on-dark) !important'},
        '& .transparent-button': {fontFamily: "'Headers', sans-serif !important"}
    },

    // ── Section-level dark background ─────────────────────────────────────────
    // Heading/subheading color on dark is handled by SectionHeader's `onDark`.
    sectionDark: {
        backgroundColor: 'var(--cs-dark-bg)',
        color: 'var(--cs-on-dark)'
    },

    // ── Hero ─────────────────────────────────────────────────────────────────
    banner: {
        // Overrides sectionDark's flat backgroundColor; falls back to it when a
        // study sets no bannerBg, so only studies opting in get a gradient.
        background: 'var(--cs-banner-bg)',
        paddingTop: '60px',
        paddingBottom: 0,
        textAlign: 'center'
    },
    bannerImage: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 'clamp(32px, 5vw, 56px)'
    },
    bannerImageInner: {
        width: '100%',
        maxWidth: 1300,
        marginInline: 'auto',
        overflow: 'hidden',
        transition: 'transform 0.4s ease',
        '&:hover': {
            transform: 'scale(1.03)'
        }
    },
    heroChipRow: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: '24px'
    },
    heroChip: {
        backgroundColor: 'var(--cs-glass-fill-strong)',
        border: '1px solid var(--cs-glass-border-35)',
        borderRadius: '999px',
        padding: '4px 16px',
        fontSize: '13px',
        color: 'var(--cs-on-dark)'
    },
    metaGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '16px',
        maxWidth: 900,
        margin: '32px auto 0'
    },
    metaPill: {
        backgroundColor: 'var(--cs-glass-fill-strong)',
        border: '1px solid var(--cs-glass-border-18)',
        borderRadius: '8px',
        padding: '10px 20px',
        fontSize: '14px',
        textAlign: 'center',
        // Allow wrapping on small screens so long values (e.g. Primary Contacts)
        // never overflow the pill / viewport; keep one-line on wider screens.
        whiteSpace: {xs: 'normal', sm: 'nowrap'},
        maxWidth: '100%'
    },
    ctaRow: {
        display: 'flex',
        gap: '16px',
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginTop: '32px'
    },

    // ── Header block + headings ──────────────────────────────────────────────
    headerBlock: {
        width: '100%',
        maxWidth: 1120,
        marginInline: 'auto',
        paddingInline: '16px',
        textAlign: 'center',
        marginBottom: '24px'
    },
    heading: {
        color: `var(--cs-heading, ${colors.text.primary})`,
        fontSize: {xs: '26px', sm: '30px', md: '34px', lg: '38px', xl: '42px'},
        lineHeight: 1.2,
        fontWeight: fontWeight.bold,
        // Honor explicit line breaks (\n) only on large screens; below lg wrap
        // naturally so authored breaks don't force awkward lines on small screens.
        whiteSpace: {xs: 'normal', lg: 'pre-line'}
    },
    subheading: {
        marginTop: '16px',
        lineHeight: 1.7,
        fontSize: '16px',
        color: `var(--cs-body, ${colors.text.primary})`
    },
    body: {
        lineHeight: 1.8,
        marginBottom: '16px'
    },

    // ── Accent / quote blocks ────────────────────────────────────────────────
    accentBlock: {
        backgroundColor: 'var(--cs-accent-bg)',
        color: 'var(--cs-accent-text)',
        borderRadius: '16px',
        padding: 'clamp(24px, 4vw, 40px)'
    },
    quoteBlock: {
        backgroundColor: 'var(--cs-quote-bg)',
        color: 'var(--cs-quote-text)',
        borderRadius: '16px',
        padding: 'clamp(16px, 3vw, 28px)',
        paddingBottom: 'clamp(12px, 2vw, 18px)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 18px 40px rgba(16, 24, 40, 0.28)'
        }
    },
    challengeSummary: {
        background: 'var(--cs-surface)',
        borderRadius: '16px',
        boxShadow: 'var(--cs-shadow-md)',
        padding: 'clamp(24px, 4vw, 40px)',
        marginTop: '32px'
    },
    quoteMark: {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: '56px',
        lineHeight: 0.6,
        opacity: 0.5,
        display: 'block',
        marginBottom: '-8px'
    },

    // ── Cards ────────────────────────────────────────────────────────────────
    card: {
        border: '1px solid var(--cs-border)',
        borderRadius: '14px',
        padding: '24px',
        height: '100%',
        background: 'var(--cs-surface)',
        boxShadow: 'var(--cs-shadow-sm)',
        transition: 'border-color 0.2s ease',
        '&:hover': {borderColor: 'var(--cs-dark-bg)'}
    },
    cardLift: {
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s ease',
        '&:hover': {
            transform: 'translateY(-4px) scale(1.03)',
            boxShadow: 'var(--cs-shadow-md)'
        }
    },
    cardIcon: {
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        background: 'var(--cs-surface-icon)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
    },
    cardTitle: {
        fontFamily: fontFamily.heading,
        fontWeight: fontWeight.semibold,
        marginBottom: '8px',
        '@media (max-width:1100px)': {fontSize: '16px'}
    },
    cardDesc: {
        '@media (max-width:1100px)': {fontSize: '14px'}
    },

    // ── Before / After timeline ──────────────────────────────────────────────
    // A central numbered rail with the "before" state on the left and the
    // "after" state on the right. On mobile the rail moves to the left edge and
    // the two cards stack (before on top, after below) beside each node.
    baSection: {
        position: 'relative'
    },
    baTimeline: {
        position: 'relative',
        zIndex: 1,
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
    },
    baStep: {
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        // mobile: node rail (left) + stacked cards (right)
        gridTemplateColumns: 'auto 1fr',
        columnGap: '16px',
        rowGap: '16px',
        alignItems: 'stretch',
        // desktop: before | node | after
        '@media (min-width: 900px)': {
            gridTemplateColumns: '1fr auto 1fr',
            columnGap: '32px',
            alignItems: 'center'
        }
    },
    baNode: {
        position: 'relative',
        gridColumn: '1',
        gridRow: '1 / span 2',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        // Continuous rail: each segment extends half the step gap on both sides
        // so consecutive segments meet, forming one unbroken vertical line.
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-12px',
            bottom: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '2px',
            background: 'var(--cs-border)',
            zIndex: 0
        },
        '@media (min-width: 900px)': {
            gridColumn: '2',
            gridRow: 'auto',
            alignItems: 'center'
        }
    },
    baNodeInner: {
        position: 'relative',
        zIndex: 1,
        width: '52px',
        minWidth: '52px',
        borderRadius: '14px',
        background: 'var(--cs-surface)',
        border: '1px solid var(--cs-border)',
        boxShadow: 'var(--cs-shadow-sm)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '52px',
        padding: 0
    },
    baNodeArrow: {
        fontSize: '20px',
        lineHeight: 1,
        color: 'var(--cs-dark-bg)'
    },
    baCard: {
        background: 'var(--cs-surface)',
        border: '1px solid var(--cs-border)',
        borderRadius: '16px',
        boxShadow: 'var(--cs-shadow-sm)',
        padding: '20px 22px',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {transform: 'translateY(-4px) scale(1.03)', boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)'}
    },
    baCardBefore: {
        gridColumn: '2',
        gridRow: '1',
        '@media (min-width: 900px)': {gridColumn: '1', gridRow: 'auto'}
    },
    baCardAfter: {
        gridColumn: '2',
        gridRow: '2',
        background: `linear-gradient(135deg, ${colors.background.default} 0%, ${colors.background.tag} 100%)`,
        boxShadow: '0 4px 18px rgba(0, 0, 0, 0.10)',
        transition: 'transform 0.4s ease, box-shadow 0.2s ease',
        '@media (min-width: 900px)': {gridColumn: '3', gridRow: 'auto'}
    },
    baCardHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '10px'
    },
    baBadge: {
        flex: '0 0 auto',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        lineHeight: 1
    },
    baBadgeBefore: {
        background: 'var(--cs-danger-bg)',
        color: 'var(--cs-danger-icon)'
    },
    baBadgeAfter: {
        background: 'var(--cs-dark-bg)',
        color: 'var(--cs-on-dark)'
    },
    baCardLabel: {
        fontFamily: fontFamily.heading,
        fontWeight: fontWeight.bold,
        fontSize: '12px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
    },
    baLabelBefore: {color: 'var(--cs-danger-fg)'},
    baLabelAfter: {color: 'var(--cs-dark-bg)'},
    baCardText: {
        fontSize: '15px',
        lineHeight: 1.6,
        color: `var(--cs-body, ${colors.text.primary})`
    },

    // ── Feature grid "table" layout ──────────────────────────────────────────
    configTable: {
        border: '1px solid var(--cs-border)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: 'var(--cs-shadow-md)',
        // Mobile: keep the two columns and let the table swipe horizontally
        // instead of stacking, so the Area | Purpose relationship stays clear.
        '@media (max-width: 599px)': {overflowX: 'auto', WebkitOverflowScrolling: 'touch'}
    },
    configRow: {
        display: 'grid',
        // Mobile: fixed two-column layout wider than the viewport → horizontal swipe.
        gridTemplateColumns: '150px minmax(260px, 1fr)',
        borderBottom: '1px solid var(--cs-border)',
        '&:last-child': {borderBottom: 'none'},
        // head row is the first child (odd) so it is never striped; body rows
        // alternate. Matches the old `:nth-child(even):not(.configHead)`.
        '&:nth-of-type(even)': {background: 'var(--cs-surface-subtle)'},
        '@media (min-width: 600px)': {gridTemplateColumns: 'minmax(0, 280px) 1fr'}
    },
    configHead: {
        background: 'var(--cs-accent-bg)'
    },
    configHeadCell: {
        padding: '12px 16px',
        fontFamily: fontFamily.heading,
        fontSize: '12px',
        fontWeight: fontWeight.bold,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: `var(--cs-heading, ${colors.text.primary})`
    },
    configCell: {
        padding: '12px 16px'
    },

    // ── Impact cards ─────────────────────────────────────────────────────────
    impactCard: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        background: 'var(--cs-surface-muted)',
        border: '1px solid var(--cs-border)',
        borderRadius: '12px',
        padding: '20px',
        height: '100%'
    },

    // ── Roadmap panel ────────────────────────────────────────────────────────
    roadmapPanel: {
        borderRadius: '20px',
        padding: 'clamp(20px, 3vw, 28px)'
    },
    roadmapStep: {
        display: 'flex',
        gap: '14px',
        alignItems: 'flex-start',
        background: 'var(--cs-glass-fill)',
        borderRadius: '12px',
        padding: '16px 18px'
    },
    roadmapIcon: {
        flex: '0 0 auto',
        width: '34px',
        height: '34px',
        borderRadius: '8px',
        background: 'var(--cs-glass-fill-icon)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    roadmapConnector: {
        width: '2px',
        height: '18px',
        margin: '4px auto',
        background: 'var(--cs-glass-divider)'
    },

    // ── Architecture panels ──────────────────────────────────────────────────
    archPanel: {
        borderRadius: '20px',
        padding: 'clamp(28px, 4vw, 52px)',
        textAlign: 'center'
    },
    archCard: {
        background: 'var(--cs-glass-fill-weak)',
        border: '1px solid var(--cs-glass-border)',
        borderRadius: '12px',
        padding: '20px',
        height: '100%',
        textAlign: 'left'
    },
    archNum: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: 'var(--cs-accent-bg)',
        color: 'var(--cs-accent-text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: fontWeight.bold,
        marginBottom: '16px'
    },

    // ── Feature grid "steps" layout ──────────────────────────────────────────
    stepRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        padding: '20px 0',
        borderBottom: `1px solid var(--cs-border, ${colors.neutral.borderLight})`,
        '&:last-child': {borderBottom: 'none'}
    },
    stepNum: {
        flex: '0 0 auto',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        background: `var(--cs-dark-bg, ${colors.primary.main})`,
        color: `var(--cs-on-dark, ${colors.text.inverse})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: fontWeight.bold
    },

    // ── Solution alternating rows ────────────────────────────────────────────
    solutionRowLabel: {
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontWeight: fontWeight.semibold,
        opacity: 0.6,
        marginBottom: '8px'
    },

    // ── Solution pinned scroll (desktop) ─────────────────────────────────────
    solPin: {position: 'relative'},
    solPinInner: {
        display: 'flex',
        alignItems: 'center',
        padding: '40px 0',
        '@media (max-width: 899px)': {padding: 0}
    },
    solStage: {
        display: 'flex',
        gap: '56px',
        width: '100%',
        alignItems: 'center',
        '@media (max-width: 899px)': {flexDirection: 'column', gap: '24px'}
    },
    solRail: {
        position: 'relative',
        flex: '1 1 44%',
        paddingLeft: '32px',
        '@media (max-width: 899px)': {paddingLeft: 0}
    },
    solProgressTrack: {
        position: 'absolute',
        left: 0,
        top: '6px',
        width: '2px',
        height: '100%',
        borderRadius: '2px',
        backgroundColor: 'var(--cs-border)',
        '@media (max-width: 899px)': {display: 'none'}
    },
    solProgressFill: {
        position: 'absolute',
        left: 0,
        top: '6px',
        width: '2px',
        height: 0, // GSAP grows this from the first step to the last on scroll
        borderRadius: '2px',
        backgroundColor: 'var(--cs-dark-bg)',
        '@media (max-width: 899px)': {display: 'none'}
    },
    solProgressDot: {
        position: 'absolute',
        left: '50%',
        bottom: 0,
        transform: 'translate(-50%, 50%)',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: 'var(--cs-dark-bg)',
        boxShadow: '0 0 0 4px var(--cs-accent-bg)'
    },
    solDetail: {
        position: 'relative',
        minHeight: '380px',
        '@media (max-width: 899px)': {minHeight: 0}
    },
    solDetailItem: {
        '@media (min-width: 900px)': {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            opacity: 0,
            visibility: 'hidden'
        },
        '@media (max-width: 899px)': {
            position: 'static',
            marginBottom: '40px'
        }
    },
    solVisual: {
        position: 'relative',
        flex: '1 1 56%',
        minHeight: '460px',
        '@media (max-width: 899px)': {display: 'none'}
    },
    solSlide: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        visibility: 'hidden',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    solItemImage: {
        marginTop: '16px',
        '@media (min-width: 900px)': {display: 'none'}
    },

    // ── Solution rows (scale-reveal layout) ──────────────────────────────────
    solList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(48px, 8vw, 96px)'
    },
    solRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        // Below 900px (incl. the 768 swipe range) stack content above the image.
        '@media (max-width: 899px)': {flexDirection: 'column', alignItems: 'stretch', gap: '24px'},
        '&:nth-of-type(even)': {
            '@media (min-width: 900px)': {flexDirection: 'row-reverse'}
        }
    },
    solRowText: {
        flex: '1 1 30%'
    },
    solRowImage: {
        flex: '1 1 70%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transformOrigin: 'center center',
        willChange: 'transform',
        // On the stacked (≤899px) layout center the screenshot at a larger size.
        '@media (max-width: 899px)': {maxWidth: 640, marginInline: 'auto', width: '100%'}
    },
    solSectionImage: {
        width: '100%',
        maxWidth: 1000,
        marginInline: 'auto',
        marginBottom: 'clamp(40px, 6vw, 72px)',
        overflow: 'hidden',
        borderRadius: '12px',
        willChange: 'transform'
    },

    // ── Horizontal-scroll gallery (pinned) ───────────────────────────────────
    // Desktop: the pin wrapper sticks and the track translates in X on scroll.
    // Mobile (<900px): the track becomes a normal vertical stack, no pin.
    solHPin: {
        overflow: 'hidden',
        '@media (max-width: 767px)': {overflow: 'visible'}
    },
    solHTrack: {
        display: 'flex',
        flexWrap: 'nowrap',
        willChange: 'transform',
        '@media (max-width: 767px)': {flexDirection: 'column', gap: '48px', paddingBottom: '48px'}
    },
    solHPanel: {
        // Height follows the content (image + text) — no forced 100vh, so the
        // panel never adds empty vertical space above/below the slide. The pin
        // (start: center center) keeps the whole gallery centred in the viewport.
        flex: '0 0 100vw',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        paddingBlock: 'clamp(16px, 3vh, 32px)',
        '@media (max-width: 767px)': {flex: '0 0 auto', paddingBlock: 0}
    },

    // ── Tech stack rows ──────────────────────────────────────────────────────
    techRow: {
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: '16px',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: 'var(--cs-shadow-sm)',
        '@media (max-width: 899px)': {flexDirection: 'column'}
    },
    techRowLabel: {
        flex: '0 0 auto',
        minWidth: '220px',
        // When the row stacks (≤899px) the label goes full-width, so the fixed
        // minWidth must not force horizontal overflow on narrow viewports.
        '@media (max-width: 899px)': {minWidth: 0, width: '100%'},
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'var(--cs-dark-bg)',
        color: 'var(--cs-on-dark)',
        padding: '16px 24px',
        fontFamily: fontFamily.heading,
        fontWeight: fontWeight.bold
    },
    techRowChips: {
        flex: 1,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
        background: 'var(--cs-surface)',
        padding: '12px 20px'
    },
    techCardLabel: {
        fontSize: '11px',
        fontWeight: fontWeight.bold,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.text.secondary,
        marginBottom: '8px'
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        background: 'var(--cs-surface-chip)',
        borderRadius: '8px',
        padding: '8px 14px',
        fontSize: '14px'
    },

    // ── Results stat cards ───────────────────────────────────────────────────
    statCard: {
        background: 'var(--cs-glass-fill)',
        border: '1px solid var(--cs-glass-border-14)',
        borderRadius: '14px',
        padding: {xs: '20px 12px', sm: '28px 20px'},
        textAlign: 'center'
    },
    resultPoints: {marginTop: '40px'},
    resultPoint: {
        display: 'flex',
        gap: '14px',
        alignItems: 'flex-start',
        padding: '16px 0',
        borderBottom: '1px solid rgba(0, 0, 0, 0.18)',
        '&:last-child': {borderBottom: 'none'}
    },
    resultCheck: {
        flex: '0 0 auto',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: 'var(--cs-dark-bg)',
        border: '1.5px solid var(--cs-dark-bg)',
        color: 'var(--cs-on-dark)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        lineHeight: 1,
        marginTop: '3px'
    },
    statCardSolid: {
        background: 'var(--cs-dark-bg)',
        border: 'none',
        borderRadius: '14px',
        padding: {xs: '20px 12px', sm: '28px 20px'},
        textAlign: 'center',
        height: '100%',
        transition: 'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
        // Hover inverts the card: white background, theme-colored text.
        '&:hover': {
            background: 'var(--cs-surface)',
            borderColor: 'transparent',
            boxShadow: 'var(--cs-shadow-md)',
            transform: 'translateY(-4px)',
            '& .cs-statValue, & .cs-statLabel': {color: 'var(--cs-dark-bg)'}
        }
    },
    statValue: {
        fontSize: 'clamp(28px, 4vw, 40px)',
        fontWeight: fontWeight.bold,
        // The study's accent colour, so the figure reads as the highlight of the card.
        color: 'var(--cs-accent-bg)'
    },
    statLabel: {
        marginTop: '8px',
        color: 'var(--cs-on-dark)',
        opacity: 0.85,
        fontSize: '16px'
    }
} satisfies Record<string, SxProps<Theme>>;
